import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isHQ, ownBranches } from "@/lib/branchScope";
import { toast } from "sonner";
import { formatCurrency, formatDate } from "@/lib/constants";
import { maskPhone } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Printer, Download, CalendarDays } from "lucide-react";

/* Operational post-sales reports — the ones a clinic manager runs daily.
 *
 * Everything on /reports is pre-sales or board-level. None of it answers "how
 * long did patients wait yesterday" or "what did we collect, by receipt". The
 * appointments report in particular was nearly free: check-in, engaged and
 * checkout have been stamped on every appointment since the check-in flow
 * shipped and were read by nothing. */

const REPORTS = {
  appointments: {
    label: "Appointments",
    path: "/reports/appointments",
    total: (d) => d.kpis?.total,
    totalLabel: "Total appointments",
    columns: [
      ["date", "Date"], ["scheduled_at", "Scheduled", "time"],
      ["checked_in_at", "Check-in", "time"], ["waited_for", "Waited for"],
      ["engaged_at", "Engaged", "time"], ["checkout_at", "Checkout", "time"],
      ["patient", "Patient"], ["doctor", "Doctor"], ["category", "Category"],
      ["status", "Status"],
    ],
  },
  payments: {
    label: "Payments",
    path: "/reports/payments",
    total: (d) => formatCurrency(d.kpis?.collected),
    totalLabel: "Total collected",
    columns: [
      ["date", "Date"], ["receipt", "Receipt"], ["invoice", "Invoice"],
      ["patient", "Patient"], ["items", "Treatments & products"],
      ["amount", "Amount", "money"], ["method", "Mode"], ["received_by", "Received by"],
    ],
  },
  "amount-due": {
    label: "Amount Due",
    path: "/reports/amount-due",
    total: (d) => formatCurrency(d.kpis?.outstanding),
    totalLabel: "Total outstanding",
    noWindow: true,
    columns: [
      ["patient_no", "Patient ID"], ["patient", "Patient"], ["mobile", "Mobile", "phone"],
      ["invoices", "Invoices"], ["oldest_due_date", "Oldest due"],
      ["due", "Outstanding", "money"],
    ],
  },
  "invoiced-income": {
    label: "Invoiced Income",
    path: "/reports/invoiced-income",
    total: (d) => formatCurrency(d.kpis?.billed),
    totalLabel: "Total billed",
    columns: [
      ["date", "Date"], ["invoice", "Invoice"], ["patient", "Patient"],
      ["items", "Treatments & products"], ["subtotal", "Subtotal", "money"],
      ["discount", "Discount", "money"], ["tax", "Tax", "money"],
      ["total", "Total", "money"], ["paid", "Paid", "money"], ["due", "Due", "money"],
      ["status", "Status"],
    ],
  },
  "billing-summary": {
    label: "Billing Summary",
    path: "/reports/billing-summary",
    total: (d) => formatCurrency(d.kpis?.billed),
    totalLabel: "Billed in window",
    columns: [
      ["date", "Date"], ["invoices", "Invoices"], ["billed", "Billed", "money"],
      ["receipts", "Receipts"], ["collected", "Collected", "money"],
      ["gap", "Not yet collected", "money"],
    ],
  },
  "new-patients": {
    label: "New Patients",
    path: "/reports/new-patients",
    total: (d) => d.kpis?.new_patients,
    totalLabel: "New patients",
    columns: [
      ["date", "Registered"], ["patient_no", "Patient ID"], ["name", "Name"],
      ["mobile", "Mobile", "phone"], ["gender", "Gender"], ["age", "Age"],
      ["city", "City"], ["source", "Source"], ["referred_by", "Referred by"],
    ],
  },
};

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const PRESETS = [
  ["Today", () => { const d = new Date(); return [iso(d), iso(d)]; }],
  ["Last 7 days", () => { const t = new Date(); const f = new Date(); f.setDate(t.getDate() - 6); return [iso(f), iso(t)]; }],
  ["Last 30 days", () => { const t = new Date(); const f = new Date(); f.setDate(t.getDate() - 29); return [iso(f), iso(t)]; }],
  ["This month", () => { const t = new Date(); return [iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(t)]; }],
];

function fmtTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  const h = d.getHours();
  return `${h % 12 === 0 ? 12 : h % 12}:${String(d.getMinutes()).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

export default function OpsReports() {
  const { user } = useAuth();
  const [kind, setKind] = useState("appointments");
  const [range, setRange] = useState(PRESETS[3][1]());
  const [branch, setBranch] = useState("all");
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const cfg = REPORTS[kind];

  useEffect(() => {
    // Non-HQ reports are scoped server-side to the user's branches, and a
    // ?branch_id outside them is a 403 — so offer only those.
    api.get("/branches").then((r) => setBranches(ownBranches(user, r.data || []))).catch(() => {});
  }, [user]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (!cfg.noWindow) { params.from = range[0]; params.to = range[1]; }
    if (branch !== "all") params.branch_id = branch;
    api.get(cfg.path, { params })
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); toast.error(formatApiError(e.response?.data?.detail)); })
      .finally(() => setLoading(false));
  }, [cfg, range, branch]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];

  // Client-side CSV so the report can go into a spreadsheet without a backend
  // export endpoint per report.
  const exportCsv = () => {
    const head = cfg.columns.map(([, l]) => l).join(",");
    const body = rows.map((r) => cfg.columns.map(([k, , t]) => {
      const v = t === "time" ? fmtTime(r[k]) : r[k];
      const s = v == null ? "" : String(v);
      return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob([`${head}\n${body}`], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${kind}-${cfg.noWindow ? "current" : `${range[0]}_${range[1]}`}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Report</label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="mt-0.5 h-9 w-48 bg-white" data-testid="ops-report-picker"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(REPORTS).map(([k, c]) => (
                  <SelectItem key={k} value={k}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Branch</label>
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger className="mt-0.5 h-9 w-44 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isHQ(user) ? "All branches" : "All my branches"}</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!cfg.noWindow && (
            <>
              <Input type="date" className="h-9 w-36 bg-white" value={range[0]}
                     onChange={(e) => setRange([e.target.value, range[1]])} />
              <span className="pb-2 text-sm text-muted-foreground">to</span>
              <Input type="date" className="h-9 w-36 bg-white" value={range[1]}
                     onChange={(e) => setRange([range[0], e.target.value])} />
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={exportCsv} title="Download CSV"><Download className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" onClick={() => window.print()} title="Print"><Printer className="h-4 w-4" /></Button>
        </div>
      </div>

      {!cfg.noWindow && (
        <div className="flex flex-wrap gap-2">
          {PRESETS.map(([label, fn]) => {
            const [f, t] = fn();
            const active = range[0] === f && range[1] === t;
            return (
              <button key={label} onClick={() => setRange([f, t])}
                className={`rounded-lg border px-3 py-1 text-xs font-medium transition-colors ${
                  active ? "border-navy bg-navy text-white" : "border-navy/15 bg-white text-muted-foreground hover:text-navy"}`}>
                <CalendarDays className="mr-1 inline h-3 w-3" />{label}
              </button>
            );
          })}
        </div>
      )}

      <div className="rounded-xl border border-navy/10 bg-white p-6 text-center shadow-sm">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{cfg.totalLabel}</p>
        <p className="font-display text-4xl font-semibold text-navy" data-testid="ops-report-total">
          {loading ? "…" : (cfg.total(data || {}) ?? 0)}
        </p>
        {data?.kpis && kind === "appointments" && (
          <div className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <span>Visited <b className="text-ink">{data.kpis.visited}</b></span>
            <span>No-show <b className="text-ink">{data.kpis.no_show}</b> ({data.kpis.no_show_rate}%)</span>
            <span>Avg wait <b className="text-ink">{data.kpis.avg_wait}</b></span>
            <span>Longest wait <b className="text-ink">{data.kpis.max_wait}</b></span>
            <span>Revenue <b className="text-ink">{formatCurrency(data.kpis.revenue)}</b></span>
          </div>
        )}
        {data?.kpis && kind === "payments" && (
          <div className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <span>Receipts <b className="text-ink">{data.kpis.receipts}</b></span>
            <span>Average <b className="text-ink">{formatCurrency(data.kpis.avg_receipt)}</b></span>
            {Object.entries(data.by_method || {}).map(([m, v]) => (
              <span key={m} className="capitalize">{m.replace("_", " ")} <b className="text-ink">{formatCurrency(v)}</b></span>
            ))}
          </div>
        )}
        {data?.kpis && kind === "invoiced-income" && (
          <div className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <span>Invoices <b className="text-ink">{data.kpis.invoices}</b></span>
            <span>Collected against these <b className="text-green-700">{formatCurrency(data.kpis.collected_against)}</b></span>
            <span>Still outstanding <b className="text-red-600">{formatCurrency(data.kpis.outstanding)}</b></span>
            <span>Realisation <b className="text-ink">{data.kpis.realisation_rate}%</b></span>
            <span>Tax <b className="text-ink">{formatCurrency(data.kpis.tax)}</b></span>
          </div>
        )}
        {data?.kpis && kind === "billing-summary" && (
          <div className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <span>Collected <b className="text-green-700">{formatCurrency(data.kpis.collected)}</b></span>
            <span>Gap <b className="text-red-600">{formatCurrency(data.kpis.gap)}</b></span>
            <span>Invoices <b className="text-ink">{data.kpis.invoices}</b></span>
            <span>Receipts <b className="text-ink">{data.kpis.receipts}</b></span>
          </div>
        )}
        {data?.kpis && kind === "amount-due" && (
          <div className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <span>Patients <b className="text-ink">{data.kpis.patients}</b></span>
            <span>Overdue <b className="text-red-600">{data.kpis.overdue_patients}</b> patients,
              {" "}<b className="text-red-600">{formatCurrency(data.kpis.overdue_amount)}</b></span>
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border border-navy/10 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-cream/60 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2.5 text-left font-semibold">#</th>
              {cfg.columns.map(([k, l]) => (
                <th key={k} className="whitespace-nowrap px-3 py-2.5 text-left font-semibold">{l}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-navy/5">
            {loading ? (
              <tr><td colSpan={cfg.columns.length + 1} className="py-12 text-center text-muted-foreground">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={cfg.columns.length + 1} className="py-12 text-center text-muted-foreground">Nothing in this window.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="hover:bg-nude/20" data-testid="ops-report-row">
                <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                {cfg.columns.map(([k, , type]) => (
                  <td key={k} className="whitespace-nowrap px-3 py-2 text-ink">
                    {type === "time" ? fmtTime(r[k])
                      : type === "money" ? formatCurrency(r[k])
                      : (r[k] === "" || r[k] == null) ? <span className="text-muted-foreground">—</span>
                      : type === "phone" ? maskPhone(r[k])
                      : String(r[k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
